import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnDestroy,
  SimpleChanges,
} from '@angular/core'
import { GbDashboardSharedUiModule } from '@galabid/gb-dashboard/shared/ui'
import { TranslateModule } from '@ngx-translate/core'
import { RafflesFacade } from '@galabid/gb-dashboard/raffle/data-access'
import { take } from 'rxjs'
import {
  NotificationWidget,
  NotifTypeEnum,
  ProjectorNotificationData,
} from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { GbPresenterWidgetPlaceholderComponent } from '../gb-presenter-widget-placeholder/gb-presenter-widget-placeholder.component'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'
import { GbColorService } from '@galabid/shared/utils/services'
import { ItemsFacade } from '@galabid/gb-dashboard/item/data-access'

@Component({
  selector: 'gb-presenter-widget-notification',
  imports: [GbPresenterWidgetPlaceholderComponent, TranslateModule, GbDashboardSharedUiModule],
  templateUrl: './gb-presenter-widget-notification.component.html',
  styleUrl: './gb-presenter-widget-notification.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterWidgetNotificationComponent implements OnDestroy {
  @Input({ required: true }) notificationWidget!: NotificationWidget

  @Input() draggable = true

  @Input() testData = false

  activeNotification: ProjectorNotificationData | null = null

  cdr = inject(ChangeDetectorRef)
  rafflesFacade = inject(RafflesFacade)
  itemsFacade = inject(ItemsFacade)
  colorResolver = inject(GbPresenterColorResolverService)
  gbColorService = inject(GbColorService)

  private readonly notificationQueue: ProjectorNotificationData[] = []
  private readonly MAX_QUEUE_SIZE = 5

  animationState: 'shown' | 'hidden' = 'hidden'

  private hideTimeoutId: number | null = null
  private exitFallbackTimeoutId: number | null = null
  private readonly DISPLAY_DURATION_MS = 5000
  private readonly EXIT_ANIMATION_FALLBACK_MS = 600

  EXAMPLE_IMAGE_URL =
    'https://s3.us-east-1.amazonaws.com/static.galabid.com/images/3185d8dc2d4047b3bd2fabb30a232d4d.jpg'

  private readonly TEST_NOTIFICATION: ProjectorNotificationData = {
    image: this.EXAMPLE_IMAGE_URL,
    image_url: null,
    image_cover: true,
    header: 'New Bid',
    body: 'Auction Item',
    secondary_text_1: 'John Smith',
    secondary_text_2: '$123',
    font_icon: null,
    notif_event: 'bid',
  }

  NotifTypeEnum = NotifTypeEnum

  get isShowing(): boolean {
    return !!this.activeNotification
  }

  get image(): string | null {
    return this.activeNotification?.image ?? null
  }

  get image_url(): string | null {
    return this.activeNotification?.image_url ?? null
  }

  get notificationImageBackground(): string | null {
    const raw = this.image_url || this.image
    if (!raw) {
      return null
    }

    const trimmed = raw.trim()
    if (!trimmed) {
      return null
    }

    if (trimmed.startsWith('url(')) {
      return trimmed
    }

    return `url('${trimmed.replace(/'/g, "\\'")}')`
  }

  get imageCover(): boolean {
    return this.activeNotification?.image_cover ?? !this.notificationWidget?.image_contained
  }

  get notifFontSize(): string {
    const fontSize = Number(this.notificationWidget?.font_size ?? 20)
    return `${fontSize / 8}cqw`
  }

  get fontFamily(): string {
    return this.notificationWidget?.font_family || this.colorResolver.globalFontFamily() || 'Arial, sans-serif'
  }

  get fontWeight(): string {
    return this.notificationWidget?.font_weight || 'normal'
  }

  get resolvedBackgroundColor(): string {
    return this.colorResolver.resolve(this.notificationWidget?.background_color) || ''
  }

  get resolvedColor(): string {
    return this.gbColorService.getContrast(this.resolvedBackgroundColor || '#000')
  }

  get resolvedSecondaryBackgroundColor(): string {
    return this.colorResolver.resolve(this.notificationWidget?.notif_secondary_background_color) || ''
  }

  get resolvedSecondaryColor(): string {
    return this.gbColorService.getContrast(this.resolvedSecondaryBackgroundColor || '#000')
  }

  ngOnChanges(changes: SimpleChanges): void {
    const testDataChange = changes['testData']
    if (!testDataChange || testDataChange.firstChange) {
      return
    }

    if (this.testData) {
      // Show a persistent notification sample while test data is enabled.
      this.itemsFacade.allItems$.pipe(take(1)).subscribe((items) => {
        const firstItem = items?.[0]
        if (!firstItem) {
          this.showNotification(this.TEST_NOTIFICATION, { persist: true })
          return
        }

        const image = firstItem.images?.[0]?.image?.image ?? null
        const body = firstItem.name || this.TEST_NOTIFICATION.body

        this.showNotification(
          {
            ...this.TEST_NOTIFICATION,
            image,
            image_url: image,
            image_cover: firstItem.image_cover,
            body,
          },
          { persist: true },
        )
      })
      return
    }

    // Turning test mode off should hide any persistent notification.
    if (this.activeNotification) {
      this.notificationQueue.length = 0
      this.clearTimers()
      this.animationState = 'hidden'

      if (!this.hasRenderableNotificationContainer()) {
        this.activeNotification = null
        this.cdr.markForCheck()
        return
      }

      window.setTimeout(() => {
        if (!this.testData && this.animationState === 'hidden') {
          this.activeNotification = null
          this.cdr.markForCheck()
        }
      }, this.EXIT_ANIMATION_FALLBACK_MS)
      this.cdr.markForCheck()
    }
  }

  ngOnDestroy(): void {
    this.clearTimers()
  }

  private hasRenderableNotificationContainer(): boolean {
    return (
      this.notificationWidget?.notif_type === NotifTypeEnum.POPOUT_A ||
      this.notificationWidget?.notif_type === NotifTypeEnum.POPOUT_B ||
      this.notificationWidget?.notif_type === NotifTypeEnum.TOAST_A
    )
  }

  private shouldShowNotification(notification: ProjectorNotificationData): boolean {
    const widget = this.notificationWidget
    if (!widget) {
      return true
    }

    const event = (notification?.notif_event || '').toLowerCase()
    if (!event) {
      return widget.show_custom_notifications ?? true
    }

    if (event.includes('bidding_purchase') || event.includes('biddingpurchase')) {
      return widget.show_bid_purchase_notifications ?? true
    }

    if (event.includes('bid')) {
      return widget.show_bid_notifications ?? true
    }

    if (event.includes('raffle')) {
      return widget.show_raffle_notifications ?? true
    }

    if (event.includes('purchase') || event.includes('buy')) {
      return widget.show_purchase_notifications ?? true
    }

    if (event.includes('donation') || event.includes('donate') || event.includes('pledge')) {
      return widget.show_donation_notifications ?? true
    }

    return widget.show_custom_notifications ?? true
  }

  showNotification(notification: ProjectorNotificationData, opts?: { persist?: boolean }): void {
    const persist = !!opts?.persist

    if (!persist && !this.testData && !this.shouldShowNotification(notification)) {
      return
    }

    if (this.testData || persist) {
      this.clearTimers()
      this.notificationQueue.length = 0
      this.activeNotification = notification
      this.animationState = 'shown'
      this.cdr.markForCheck()
      return
    }

    this.enqueueNotification(notification)

    if (this.activeNotification) {
      return
    }

    this.showNextQueuedNotification()
    this.cdr.markForCheck()
  }

  onNotificationAnimationEnd(event: AnimationEvent): void {
    if (event.target !== event.currentTarget) {
      return
    }

    if (this.animationState !== 'hidden') {
      return
    }

    const animationName = event.animationName ?? ''
    const isExitAnimation = animationName === 'notif-popout-out' || animationName === 'notif-toast-out'

    if (!isExitAnimation) {
      return
    }

    this.handleExitCompleted()
  }

  private enqueueNotification(notification: ProjectorNotificationData): void {
    if (this.notificationQueue.length >= this.MAX_QUEUE_SIZE) {
      this.notificationQueue.splice(0, 1)
    }

    this.notificationQueue.push(notification)
  }

  private showNextQueuedNotification(): void {
    this.clearTimers()

    const next = this.notificationQueue.shift()
    if (!next) {
      return
    }

    this.activeNotification = next
    this.animationState = 'shown'
    this.cdr.markForCheck()

    this.hideTimeoutId = window.setTimeout(() => {
      this.animationState = 'hidden'
      this.scheduleExitFallback()
      this.cdr.markForCheck()
    }, this.DISPLAY_DURATION_MS)
  }

  private scheduleExitFallback(): void {
    if (this.exitFallbackTimeoutId) {
      window.clearTimeout(this.exitFallbackTimeoutId)
    }

    this.exitFallbackTimeoutId = window.setTimeout(() => {
      this.exitFallbackTimeoutId = null

      // Only run the fallback if we're still in the hidden state.
      if (this.animationState !== 'hidden') {
        return
      }

      this.handleExitCompleted()
    }, this.EXIT_ANIMATION_FALLBACK_MS)
  }

  private handleExitCompleted(): void {
    // Exit animation finished (or fallback fired) -> either show next queued notification or clear completely.
    this.activeNotification = null
    this.clearTimers()

    if (this.testData) {
      this.cdr.markForCheck()
      return
    }

    if (this.notificationQueue.length > 0) {
      this.showNextQueuedNotification()
      return
    }

    // Only clear once the exit animation has fully completed, so the placeholder doesn't flash early.
    this.cdr.markForCheck()
  }

  private clearTimers(): void {
    if (this.hideTimeoutId) {
      window.clearTimeout(this.hideTimeoutId)
      this.hideTimeoutId = null
    }

    if (this.exitFallbackTimeoutId) {
      window.clearTimeout(this.exitFallbackTimeoutId)
      this.exitFallbackTimeoutId = null
    }
  }
}
